import { appConfig } from "./app-config.js";

/**
 * **Welches System habe ich hier offen?**
 *
 * Stage und Produktion sehen bis auf die Adresse gleich aus — dieselbe
 * Oberfläche, dieselbe Marke, dieselben Daten dem Anschein nach. Wer beide
 * nebeneinander offen hat (und das hat, wer stage benutzt, immer), tippt
 * irgendwann im falschen Fenster. Beantwortet wird die Frage ohne Klick vom
 * **Symbol im Tab**: dieselbe Marke in einem dicken orangen Rahmen
 * (`favicon-stage.svg`).
 *
 * **Der Seitentitel trägt das Etikett nicht (mehr).** Er benennt seit
 * `modules/page-title.ts` die offene Seite, und der Platz vorn gehört diesem
 * Namen — ein „STAGE ·" davor kostete in jedem Tab genau die Zeichen, wegen
 * derer man hinsieht. Das Symbol steht ohnehin daneben und überlebt auch den
 * schmalen Tab, in dem vom Titel nichts mehr zu sehen ist.
 *
 * **Die Umgebung kommt aus `/api/config`, nicht aus dem Build.** Es gibt ein
 * Bündel für beide Systeme — dieselbe Entscheidung wie bei der Sentry-DSN. Ein
 * eingebackener Wert hieße zwei Builds, und der falsche wäre nicht zu bemerken.
 *
 * **Alles außer `production` gilt als markierungswürdig.** Die Vorbelegung ist
 * leer (also unmarkiert), damit eine lokale Entwicklungsinstanz nicht plötzlich
 * ein Etikett trägt; wer eine Umgebung *benennt*, meint damit „nicht die
 * Produktion".
 */

/** Vorbelegtes Etikett je Umgebungsname; alles andere wird großgeschrieben. */
const LABELS: Readonly<Record<string, string>> = { stage: "STAGE", staging: "STAGE" };

/** Diese Namen sind die Produktion — sie bekommen nichts. */
const PRODUCTION = new Set(["production", "prod", "live"]);

let applied = false;

/**
 * Das Symbol tauschen, falls dies nicht die Produktion ist. Nicht abzuwarten:
 * Die Auskunft ist ein Zusatz, die Oberfläche wartet nicht auf sie.
 */
export async function applyDeploymentMarks(): Promise<void> {
  // Ein zweiter Aufruf tauschte die Symbole ein zweites Mal aus.
  if (applied) return;
  const config = await appConfig();
  // `deploymentLabel` entscheidet weiterhin, OB markiert wird — der Name selbst
  // steht nirgends mehr in der Oberfläche.
  if (!deploymentLabel(config?.environment)) return;
  applied = true;
  swapFavicon();
}

/** `"staging"` → `"STAGE"`, `"production"` → `null`. */
export function deploymentLabel(environment: unknown): string | null {
  const name = String(environment ?? "").trim().toLowerCase();
  if (!name || PRODUCTION.has(name)) return null;
  return LABELS[name] ?? name.toUpperCase();
}

/**
 * Die Symbole austauschen. **Ersetzt statt umgeschrieben:** Ein `href`, das
 * sich ändert, liest nicht jeder Browser noch einmal ein — ein neu eingehängtes
 * `<link>` schon. Die Reihenfolge ist die aus `index.html`; der Browser sucht
 * sich daraus die Fassung, die er mag.
 */
function swapFavicon(): void {
  for (const link of document.head.querySelectorAll('link[rel~="icon"]')) link.remove();
  document.head.append(
    iconLink("/favicon-stage.ico", "", "any"),
    iconLink("/favicon-stage.svg", "image/svg+xml"),
    iconLink("/icons/icon-192-stage.png", "image/png"),
  );
}

function iconLink(href: string, type: string, sizes?: string): HTMLLinkElement {
  const link = document.createElement("link");
  link.rel = "icon";
  link.href = href;
  if (type) link.type = type;
  if (sizes) link.setAttribute("sizes", sizes);
  return link;
}
